import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Beaker, CheckCircle2, ClipboardList, Droplets, Loader2, MapPin, Phone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Phase C — Tank Water Health Checks in the technician field app.
 *
 * The red COLLECT AWARE WATER SAMPLE DURING VISIT banner shows ONLY when
 * the server says `collect_sample` (paid lab order + method
 * technician_site_collection + status to_collect). Once collected it
 * switches to a green SAMPLE COLLECTED state and stops being actionable.
 *
 * Nov 2026 — "My Jobs" stays based on AWARE's own assigned_technician_id
 * (never raw ServiceM8 scheduling on its own). "Refresh jobs" pulls this
 * technician's own open appointments fresh from ServiceM8 on demand,
 * instead of waiting for the next background poll tick.
 */
export const FieldHealthChecks = ({ techs = [], canAssign = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = () => api.get("/field/health-checks")
    .then(({ data: d }) => setData(d))
    .catch(() => setData({ health_checks: [] }));

  useEffect(() => { load(); }, []); // eslint-disable-line

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post("/field/health-checks/refresh");
      setData(d);
      toast.success(d.synced_appointments
        ? `Synced ${d.synced_appointments} job${d.synced_appointments === 1 ? "" : "s"} from ServiceM8`
        : "Jobs refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh from ServiceM8");
    } finally { setRefreshing(false); }
  };

  const collect = async (ref) => {
    setBusy(ref);
    const coverKey = `hc_cover_${ref}`;
    const already = sessionStorage.getItem(coverKey) === "1";
    try {
      const { data: r } = await api.post(`/field/health-checks/${ref}/collect-sample`,
        null, { params: already ? { confirm_cover: true } : {} });
      toast.success(r.already ? "Already recorded as collected" : "Sample marked collected");
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 422 && d?.error === "cover_confirmation_required") {
        if (window.confirm(`${d.message}\n\nThe AWARE/ServiceM8 assignment won't change.`)) {
          try {
            sessionStorage.setItem(coverKey, "1");
            await api.post(`/field/health-checks/${ref}/collect-sample`, null,
              { params: { confirm_cover: true } });
            toast.success("Sample marked collected"); await load();
          } catch (e2) { toast.error(e2.response?.data?.detail || "Could not mark the sample collected"); }
        }
      } else {
        toast.error(typeof d === "string" ? d : "Could not mark the sample collected");
      }
    } finally { setBusy(""); }
  };

  const assign = async (ref, tid) => {
    try {
      await api.put(`/admin/tank-water-health-check/bookings/${ref}/assign`,
        { technician_id: tid || null });
      toast.success(tid ? "Technician assigned" : "Health Check unassigned");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };

  if (!data) return (
    <div className="mt-8 flex justify-center" data-testid="field-health-checks-loading">
      <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
    </div>
  );
  const rows = data.health_checks || [];

  return (
    <div className="mt-8" data-testid="field-health-checks">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-500 flex items-center gap-1.5">
          <Droplets className="w-4 h-4" /> Tank Water Health Checks
        </h2>
        <button type="button" disabled={refreshing} onClick={refresh}
          data-testid="field-hc-refresh-jobs"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300
                     bg-white px-3 py-1.5 text-[11px] font-bold text-slate-600 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh jobs"}
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="mt-3 rounded-2xl bg-white border border-slate-200 p-6 text-center text-sm text-slate-400"
          data-testid="field-health-checks-empty">
          No Tank Water Health Checks scheduled.
        </div>
      ) : (
        <div className="mt-3 space-y-3" data-testid="field-health-checks-list">
          {rows.map((h) => (
            <div key={h.reference} data-testid={`field-hc-card-${h.reference}`}
              className={`rounded-2xl bg-white border p-4 shadow-sm ${
                h.collect_sample ? "border-rose-300 ring-2 ring-rose-200" : "border-slate-200"}`}>
              {h.collect_sample && (
                <div data-testid={`field-hc-collect-banner-${h.reference}`}
                  className="mb-3 -mx-4 -mt-4 rounded-t-2xl bg-rose-600 px-4 py-2.5 text-white
                             text-[12px] font-extrabold tracking-wide flex items-center gap-2">
                  <Beaker className="w-4 h-4" /> {h.collection_instruction}
                </div>
              )}
              {h.lab?.collection_status === "collected" && (
                <div data-testid={`field-hc-collected-banner-${h.reference}`}
                  className="mb-3 -mx-4 -mt-4 rounded-t-2xl bg-emerald-600 px-4 py-2.5 text-white
                             text-[12px] font-extrabold tracking-wide flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> AWARE SAMPLE COLLECTED
                </div>
              )}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-slate-900 text-[15px]"
                    data-testid={`field-hc-customer-${h.reference}`}>{h.customer_name}</div>
                  <div className="text-[12px] text-slate-500 flex items-start gap-1 mt-0.5">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span data-testid={`field-hc-address-${h.reference}`}>{h.property_address}</span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1"
                    data-testid={`field-hc-appointment-${h.reference}`}>
                    {h.date_display} · {h.time_display}
                  </div>
                  {h.mobile && (
                    <a href={`tel:${h.mobile}`} className="mt-1 inline-flex items-center gap-1 text-[12px] font-bold text-cyan-700">
                      <Phone className="w-3.5 h-3.5" /> {h.mobile}
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{h.reference}</div>
                  {h.is_test && <div className="text-[10px] font-bold text-amber-600">TEST</div>}
                </div>
              </div>

              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px]">
                {h.lab?.purchased ? (
                  <>
                    <div className="font-bold text-slate-800" data-testid={`field-hc-package-${h.reference}`}>
                      {h.lab.test_name}
                    </div>
                    <div className="text-slate-500 mt-0.5" data-testid={`field-hc-lab-ref-${h.reference}`}>
                      Lab order {h.lab.reference}
                    </div>
                    <div className="text-slate-500 mt-0.5" data-testid={`field-hc-collection-status-${h.reference}`}>
                      Collection status: <b className="text-slate-700">{h.lab.collection_status || "—"}</b>
                    </div>
                  </>
                ) : (
                  <div className="text-slate-500" data-testid={`field-hc-no-lab-${h.reference}`}>
                    No AWARE lab test added — nothing to collect on this visit.
                  </div>
                )}
              </div>

              <button data-testid={`field-hc-open-${h.reference}`}
                onClick={() => navigate(`/field/health-check/${h.reference}`)}
                className="mt-3 w-full py-3 rounded-full font-extrabold text-[13px] text-white
                           bg-slate-900 hover:bg-slate-800 flex items-center justify-center gap-2">
                <ClipboardList className="w-4 h-4" />
                {h.field_form_status === "completed" ? "VIEW HEALTH CHECK"
                  : h.field_form_status === "in_progress" ? "RESUME HEALTH CHECK"
                    : "OPEN HEALTH CHECK"}
              </button>

              {h.collect_sample && (
                <button data-testid={`field-hc-collect-btn-${h.reference}`}
                  disabled={busy === h.reference}
                  onClick={() => collect(h.reference)}
                  className="mt-3 w-full py-2.5 rounded-full font-extrabold text-[12px] text-white
                             bg-rose-600 hover:bg-rose-700 disabled:opacity-60">
                  {busy === h.reference ? "Saving…" : "MARK SAMPLE COLLECTED"}
                </button>
              )}

              {canAssign && (
                <select data-testid={`field-hc-assign-${h.reference}`}
                  value={h.assigned_technician_id || ""}
                  onChange={(e) => assign(h.reference, e.target.value)}
                  className="mt-3 w-full text-[12px] border border-slate-200 rounded-lg px-2 py-2 bg-white">
                  <option value="">UNASSIGNED</option>
                  {techs.map((t) => (
                    <option key={t.id} value={t.id}>{t.name || t.email}</option>
                  ))}
                </select>
              )}
              {!canAssign && !h.assigned_technician_id && (
                <div className="mt-2 text-[11px] font-bold text-amber-600"
                  data-testid={`field-hc-unassigned-${h.reference}`}>UNASSIGNED</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
